import { Menu } from "../../ui/layout/Menu";
import React, { useEffect, useState } from "react";
import useApi from "../../hooks/useApi";
import { userState } from "../../store";
import { useSnapshot } from "valtio";
import { FooterCmp } from "../../ui/layout/Footer";
import { useParams } from "react-router";
import { ApiError, CompareTipWithRelations, TipWithRelations, UserWithRelations } from "../../@backend";
import { Tooltip } from "flowbite-react";
import { JokerButton } from "../../ui/buttons/JokerButton";
import { GrCompare } from "react-icons/gr";
import { MdCompare } from "react-icons/md";
import { PageHeader } from "../../ui/ds/PageHeader";
import { Badge } from "../../ui/ds/Badge";

export const UserProfile = () => {
    const { id } = useParams();
    const apiUrl = process.env.REACT_APP_API_URL ?? 'http://localhost:3000';
    const userSnap = useSnapshot(userState);
    const { backend } = useApi(true);
    const [user, setUser] = useState<UserWithRelations | null>(null);
    const [tips, setTips] = useState<TipWithRelations[] | CompareTipWithRelations[]>([] as TipWithRelations[]);
    const [compareMode, setCompareMode] = useState<boolean>(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const loadProfile = () => {
        backend.ladder.ladderControllerGetUserInLadder(id, Number(process.env.REACT_APP_ACTIVE_TOURNAMENT_ID)).then((res) => {
            setUser(res.profile);
            setTips(res.tips);
        }).catch((err: ApiError) => {
            setErrorMessage(err.body.error.message);
        });
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (userSnap.isLoggedIn && userSnap.userProfile.entryFeePaid) {
            loadProfile();
        }
    }, [userSnap.isLoggedIn]);

    if (!userSnap.isLoggedIn) {
        return (
            <>
                <Menu />
                <div className="flex flex-col items-center justify-center min-h-[40vh] gap-4 p-8">
                    <p className="tb-h2 text-text-muted">Je vyžadováno přihlášení</p>
                </div>
                <FooterCmp />
            </>
        );
    }

    const compareWithMe = () => {
        backend.ladder.ladderControllerCompareUserWithMe(id).then((comparedTips) => {
            setTips(comparedTips);
            setCompareMode(true);
        }).catch((err: ApiError) => {
            setErrorMessage(err.body.error.message);
        });
    };

    const initials = user
        ? `${user.firstname?.charAt(0) ?? ''}${user.lastname?.charAt(0) ?? ''}`
        : '?';

    return (
        <>
            <Menu />
            <PageHeader
                title={user ? `${user.firstname} ${user.lastname}` : 'Profil hráče'}
                subtitle={user ? `${user.points ?? 0} bodů` : ''}
            />

            <div className="max-w-4xl mx-auto px-4 md:px-8 py-6 flex flex-col gap-4">

                {errorMessage && (
                    <div className="bg-semantic-danger/10 border border-semantic-danger/30 rounded-card px-4 py-3">
                        <strong className="font-bold text-semantic-danger font-nunito">Chyba!</strong>
                        <span className="tb-body ml-2">{errorMessage}</span>
                    </div>
                )}

                {user && (
                    <div className="bg-bg-card rounded-content shadow-card p-5 md:p-6">
                        <div className="flex flex-row gap-5 items-center">
                            {/* Avatar */}
                            <div className="flex-shrink-0">
                                {user.avatar ? (
                                    <img
                                        src={`${apiUrl}/avatars/${user.avatar}`}
                                        alt="avatar"
                                        className="w-20 h-20 md:w-28 md:h-28 rounded-full object-cover border-2 border-border-neutral"
                                    />
                                ) : (
                                    <div className="w-20 h-20 md:w-28 md:h-28 rounded-full bg-bg-input border-2 border-border-neutral flex items-center justify-center">
                                        <span className="text-3xl md:text-4xl font-bold text-text-muted font-nunito">{initials}</span>
                                    </div>
                                )}
                            </div>

                            {/* Info */}
                            <div className="flex flex-col gap-2">
                                <h2 className="tb-h1 text-text-primary">{user.firstname} {user.lastname}</h2>
                                <div className="flex items-center gap-2">
                                    <span className="tb-h3 text-brand-blue font-black">{user.points ?? 0}</span>
                                    <span className="tb-caption">bodů</span>
                                </div>
                                <div className="tb-caption">
                                    Natipováno zápasů: <strong className="text-text-primary">{tips.length}</strong>
                                </div>
                                {user?.bonusQuestion && (
                                    <div className="flex items-center gap-2">
                                        <img
                                            src={`/images/flags/${user.bonusQuestion.flag}`}
                                            alt={user.bonusQuestion.nameCountry}
                                            className="w-8 h-auto"
                                        />
                                        <span className="tb-body">{user.bonusQuestion.nameCountry}</span>
                                        {user?.bonusQuestionAnswer === 133909 && (
                                            <Badge variant="success">+50 b</Badge>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {tips.length > 0 && (
                    <div className="bg-bg-card rounded-content shadow-card overflow-hidden">
                        {/* Table header */}
                        <div className="flex flex-row justify-between items-center px-5 py-4 border-b border-border-neutral">
                            <h2 className="tb-h3 text-text-primary">Tipy</h2>
                            {!compareMode ? (
                                <button
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-btn bg-brand-blue/10 text-brand-blue text-[13px] font-bold font-nunito transition-colors hover:bg-brand-blue/20"
                                    onClick={compareWithMe}
                                >
                                    <MdCompare className="text-base" /> Porovnat se mnou
                                </button>
                            ) : (
                                <button
                                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-btn bg-brand-orange/10 text-brand-orange text-[13px] font-bold font-nunito transition-colors hover:bg-brand-orange/20"
                                    onClick={() => setCompareMode(false)}
                                >
                                    <GrCompare className="text-base" /> Zrušit porovnání
                                </button>
                            )}
                        </div>

                        <div className="overflow-x-auto">
                            <table className="table-auto w-full text-[14px] font-nunito">
                                <thead>
                                    <tr className="bg-bg-app">
                                        <th className="px-3 py-2.5 text-left font-bold text-text-muted border-b border-border-neutral">Domácí</th>
                                        <th className="px-3 py-2.5 text-left font-bold text-text-muted border-b border-border-neutral">Hosté</th>
                                        <th className="px-3 py-2.5 text-center font-bold text-text-muted border-b border-border-neutral">Tip</th>
                                        <th className="px-3 py-2.5 text-center font-bold text-text-muted border-b border-border-neutral">Výsledek</th>
                                        <th className="px-3 py-2.5 text-center font-bold text-text-muted border-b border-border-neutral">Body</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {tips.map((tip) => (
                                        <tr key={tip.id} className="border-b border-divider-row hover:bg-bg-app/60 transition-colors">
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-2">
                                                    <img src={`/images/flags/${tip.match?.teamHome.flag}`} alt={tip.match?.teamHome.name} className="w-7 h-auto flex-shrink-0" />
                                                    <span className="font-semibold text-text-primary">{tip.match?.teamHome?.nameCountry}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2">
                                                <div className="flex items-center gap-2">
                                                    <img src={`/images/flags/${tip.match?.teamAway.flag}`} alt={tip.match?.teamAway.name} className="w-7 h-auto flex-shrink-0" />
                                                    <span className="font-semibold text-text-primary">{tip.match?.teamAway?.nameCountry}</span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <div className="font-bold text-text-primary">{tip.tipHomeTeam} : {tip.tipAwayTeam}</div>
                                                {compareMode && (
                                                    <div className="mt-0.5 rounded bg-brand-orange/15 text-brand-orange text-[12px] font-bold px-1">
                                                        {(tip as CompareTipWithRelations).compareTipHomeTeam} : {(tip as CompareTipWithRelations).compareTipAwayTeam}
                                                    </div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-center font-bold text-text-primary">
                                                {tip.match.locked ? `${tip.match.scoreHomeTeam} : ${tip.match.scoreAwayTeam}` : '–'}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                                <div className="flex flex-col items-center gap-1">
                                                    {tip.points >= 0 && tip.points !== null ? (
                                                        <>
                                                            <span className="font-bold text-semantic-success">{tip.points}</span>
                                                            {compareMode && (
                                                                <span className="rounded bg-brand-orange/15 text-brand-orange text-[12px] font-bold px-1">
                                                                    {(tip as CompareTipWithRelations).comparePoints}
                                                                </span>
                                                            )}
                                                        </>
                                                    ) : (
                                                        <Tooltip content="Zápas ještě není vyhodnocen">
                                                            <span className="text-text-muted cursor-help">⏳</span>
                                                        </Tooltip>
                                                    )}
                                                    {tip.joker && <JokerButton />}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {user?.bonusQuestionAnswer === 133909 && (
                            <div className="flex justify-end px-5 py-3 border-t border-border-neutral">
                                <Tooltip content="Správně zodpovězená bonusová otázka">
                                    <Badge variant="success">Bonusová otázka +50 b</Badge>
                                </Tooltip>
                            </div>
                        )}
                    </div>
                )}
            </div>

            <FooterCmp />
        </>
    );
};
